// ===========================================================================
// Vitals Data for Display
// ===========================================================================

interface VitalsDisplayBaseData {
  effectiveDateTime: string;
}

/**
 * A generic type for vitals that consist of a value and a unit,
 * such as height, weight, temperature, etc.
 */
export interface ValueUnitDisplayData extends VitalsDisplayBaseData {
  value: number | null;
  unit: string;
}

export interface ComponentDisplayData extends VitalsDisplayBaseData {
  values: [number, number] | null;
}

// ===========================================================================
// Vitals Data for Mutation (API Params)
// ===========================================================================

interface VitalsObservationBaseParams {
  patient_id?: string;
  reported_date: string;
}

/**
 * A generic type for creating "value-unit" observations.
 * It uses an index signature to be flexible for different vitals.
 */
export interface ValueUnitObservationParams
  extends VitalsObservationBaseParams {
  [key: string]: number | string | undefined;
}

export interface HeightObservationParams extends VitalsObservationBaseParams {
  height: number;
  unit: "inch" | "cm";
}

export interface WeightObservationParams extends VitalsObservationBaseParams {
  weight: number;
  unit: "lbs";
}

export interface BpObservationParams extends VitalsObservationBaseParams {
  systolic_pressure: number;
  diastolic_pressure: number;
}

export interface HeartRateObservationParams
  extends VitalsObservationBaseParams {
  heart_rate: number;
}

export interface BodyTemperatureObservationParams
  extends VitalsObservationBaseParams {
  body_temperature: number;
}

export interface HeadCircumferenceObservationParams
  extends VitalsObservationBaseParams {
  head_circumference: number;
}

// ===========================================================================
// General & Union Types
// ===========================================================================

export enum VitalsEnum {
  HEIGHT = "height",
  WEIGHT = "weight",
  BLOOD_PRESSURE = "blood-pressure",
  HEART_RATE = "heart-rate",
  BODY_TEMPERATURE = "body-temperature",
  HEAD_CIRCUMFERENCE = "head-circumference",
}

export type VitalsType = keyof VitalDataMap;

export type VitalDataMap = {
  [VitalsEnum.HEIGHT]: ValueUnitDisplayData;
  [VitalsEnum.WEIGHT]: ValueUnitDisplayData;
  [VitalsEnum.BLOOD_PRESSURE]: ComponentDisplayData;
  [VitalsEnum.HEART_RATE]: ValueUnitDisplayData;
  [VitalsEnum.BODY_TEMPERATURE]: ValueUnitDisplayData;
  [VitalsEnum.HEAD_CIRCUMFERENCE]: ValueUnitDisplayData;
};

export type VitalParamsMap = {
  [VitalsEnum.HEIGHT]: HeightObservationParams;
  [VitalsEnum.WEIGHT]: WeightObservationParams;
  [VitalsEnum.BLOOD_PRESSURE]: BpObservationParams;
  [VitalsEnum.HEART_RATE]: HeartRateObservationParams;
  [VitalsEnum.BODY_TEMPERATURE]: BodyTemperatureObservationParams;
  [VitalsEnum.HEAD_CIRCUMFERENCE]: HeadCircumferenceObservationParams;
};

export type VitalsObservationParams = VitalParamsMap[VitalsType];

export type VitalFormData = {
  value: string;
  effectiveDateTime: string;
};

// ===========================================================================
// LOINC Code Mapping for Vitals
// ===========================================================================

export const VITALS_CODE_MAPPING: Record<
  VitalsType,
  { code: string; system: string }
> = {
  [VitalsEnum.HEIGHT]: { code: "8302-2", system: "http://loinc.org" },
  [VitalsEnum.WEIGHT]: { code: "29463-7", system: "http://loinc.org" },
  [VitalsEnum.BLOOD_PRESSURE]: { code: "85354-9", system: "http://loinc.org" },
  [VitalsEnum.HEART_RATE]: { code: "8867-4", system: "http://loinc.org" },
  [VitalsEnum.BODY_TEMPERATURE]: { code: "8310-5", system: "http://loinc.org" },
  [VitalsEnum.HEAD_CIRCUMFERENCE]: {
    code: "9843-4",
    system: "http://loinc.org",
  },
};

// ===========================================================================
// Display Names for Vitals
// ===========================================================================

export const VITAL_DISPLAY_NAMES: Record<VitalsType, string> = {
  [VitalsEnum.HEIGHT]: "Height",
  [VitalsEnum.WEIGHT]: "Weight",
  [VitalsEnum.BLOOD_PRESSURE]: "BP",
  [VitalsEnum.HEART_RATE]: "Heart Rate",
  [VitalsEnum.BODY_TEMPERATURE]: "Temperature",
  [VitalsEnum.HEAD_CIRCUMFERENCE]: "Head Circumference",
};
